import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import type {
  AriaComponentOption,
  GridComponentOption,
  LegendComponentOption,
  TooltipComponentOption,
} from 'echarts/components';
import {
  AriaComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import type { ComposeOption } from 'echarts/core';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import type { CallbackDataParams, TopLevelFormatterParams } from 'echarts/types/dist/shared';
import ReactEChartsCoreDefault from 'echarts-for-react/lib/core';

// Tree-shaken core registration, shared by every ECharts-based chart in the
// app. Register a chart/component here once and every chart that imports
// `echarts` from this module can use it — don't re-register in individual
// chart files. GraphicComponent/LabelLayout aren't registered because no
// chart in the app uses them; add them back if a future chart needs custom
// graphic overlays or label-overlap avoidance.
echarts.use([
  PieChart,
  LineChart,
  // An unregistered series type is dropped *silently*: the grid and axes still
  // draw, so a bar chart renders as an empty frame with category labels and no
  // legend. That was the compare viewer's grouped bars for a while.
  BarChart,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GridComponent,
  AriaComponent,
  SVGRenderer,
]);

export { echarts };

// echarts-for-react/lib/core is CommonJS (`exports.default = EChartsReactCore`,
// no `exports` map in its package.json). Under Rolldown/Vite's production
// build the default import can resolve to the module namespace object
// ({ default: Component, __esModule: true }) instead of the component
// itself, crashing render with React error #130 ("Element type is invalid
// ... but got: object") — confirmed empirically, dev mode and vitest (which
// mocks this module) don't surface it. Normalize the interop shape so it's
// always the component regardless of how the bundler resolved it.
const ReactEChartsCore =
  (ReactEChartsCoreDefault as { default?: typeof ReactEChartsCoreDefault }).default ??
  ReactEChartsCoreDefault;
export const ReactECharts = ReactEChartsCore;

// Shared option type + tooltip-param normalization for the axis-trigger area
// charts (MetricsTrends, TrendChart) — both are a single line/area series
// over a Grid, so they compose the same set of option pieces.
export type AreaLineChartOption = ComposeOption<
  | LineSeriesOption
  | TooltipComponentOption
  | LegendComponentOption
  | GridComponentOption
  | AriaComponentOption
>;

// 'axis'-trigger tooltip formatters receive one param per series at that x
// value; these charts only ever have one series, so this always resolves to
// that single point regardless of which shape ECharts hands back.
export function firstTooltipParam(params: TopLevelFormatterParams): CallbackDataParams {
  return Array.isArray(params) ? params[0] : params;
}

// Multi-series axis-trigger charts need every param at the hovered x, not just
// the first — normalized to an array so the formatter can list each series.
export function tooltipParams(params: TopLevelFormatterParams): CallbackDataParams[] {
  return Array.isArray(params) ? params : [params];
}

// Option type for the categorical bar charts (defect due-date histogram).
export type CategoryBarChartOption = ComposeOption<
  | BarSeriesOption
  | TooltipComponentOption
  | LegendComponentOption
  | GridComponentOption
  | AriaComponentOption
>;
